import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  FloatingPortal,
  offset,
  shift,
  useFloating,
  useTransitionStatus,
} from '@floating-ui/react';
import cn from 'classnames';
import { useRef } from 'react';

import { defaultValueLabelFormat, toPercent } from '../utils';

import type { Placement } from '@floating-ui/react';
import type * as React from 'react';
import type { ReactElement } from 'react';

import type { Axis, AxisProps, GetHiddenInputProps, GetThumbProps, SliderProps } from '../types';

import '../../../../popovers/tooltip/tooltip.scss';

interface SliderThumbProps {
  active: number;
  ariaLabel?: string;
  ariaLabelledby?: string;
  ariaValuetext?: string;
  axis: Axis;
  axisProps: AxisProps;
  focusedThumbIndex: number;
  getAriaValueText?: (value: number, index: number) => string;
  getHiddenInputProps: GetHiddenInputProps;
  getThumbProps: GetThumbProps;
  getThumbStyle: (index: number) => React.CSSProperties;
  index: number;
  max: number;
  min: number;
  open: number;
  readOnly: boolean;
  scale?: (value: number) => number;
  value: number;
  valueLabelDisplay: SliderProps['valueLabelDisplay'];
  valueLabelFormat?: SliderProps['valueLabelFormat'];
  values: number[];
}

/*
 * Renders a single slider thumb with its optional value label and hidden range input.
 *
 * The thumb is absolutely positioned along the track via inline styles computed from
 * `axisProps`. It contains:
 * - A visible draggable handle (the `<span>` with `rbui-slider__thumb` class).
 * - An optional floating value label displayed on hover/focus or permanently.
 * - A visually hidden `<input type="range">` that provides native keyboard accessibility.
 */
export const SliderThumb = ({
  active,
  ariaLabel,
  ariaLabelledby,
  ariaValuetext,
  axis,
  axisProps,
  focusedThumbIndex,
  getAriaValueText,
  getHiddenInputProps,
  getThumbProps,
  getThumbStyle,
  index,
  max,
  min,
  open,
  readOnly,
  scale,
  value,
  valueLabelDisplay = 'off',
  valueLabelFormat = defaultValueLabelFormat,
  values,
}: SliderThumbProps): ReactElement => {
  const arrowRef = useRef(null);
  const percent = toPercent(value, min, max);

  const thumbStyle = {
    ...getThumbStyle(index),
    ...axisProps[axis].offset(percent),
  };

  const isOpen = open === index || valueLabelDisplay === 'on';
  const showValueLabel = valueLabelDisplay === 'on' || (valueLabelDisplay === 'auto' && isOpen);
  const displayValue = scale ? scale(value) : value;
  const formattedLabel = valueLabelFormat(displayValue, index);
  const resolvedAriaValuetext = getAriaValueText
    ? getAriaValueText(displayValue, index)
    : (ariaValuetext ?? (scale ? String(displayValue) : undefined));

  const placement: Placement = axis === 'vertical' ? 'right' : 'top';

  const { context, floatingStyles, refs } = useFloating({
    middleware: [offset(12), flip(), shift(), arrow({ element: arrowRef })],
    open: showValueLabel,
    placement,
    whileElementsMounted: autoUpdate,
  });

  const { isMounted, status } = useTransitionStatus(context);

  return (
    <span
      {...getThumbProps({ 'data-index': index })}
      className={cn('rbui-slider__thumb', {
        'rbui-slider__thumb--active': active === index,
        'rbui-slider__thumb--focus-visible': focusedThumbIndex === index,
      })}
      data-index={index}
      ref={refs.setReference}
      style={thumbStyle}
    >
      {isMounted && (
        <FloatingPortal>
          <div
            className="rbui-tooltip"
            data-placement={context.placement}
            ref={refs.setFloating}
            style={{ ...floatingStyles, pointerEvents: 'none' }}
          >
            <div className={cn('rbui-tooltip__inner', `rbui-tooltip__inner--${status}`)}>
              {formattedLabel}
              <FloatingArrow
                className="rbui-tooltip__arrow"
                context={context}
                height={8}
                ref={arrowRef}
                tipRadius={2}
                width={16}
              />
            </div>
          </div>
        </FloatingPortal>
      )}
      {/* eslint-disable-next-line jsx-a11y/role-supports-aria-props -- The hidden input has role="slider" via getHiddenInputProps */}
      <input
        {...getHiddenInputProps({ 'data-index': index })}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        aria-readonly={readOnly || undefined}
        aria-valuetext={resolvedAriaValuetext}
        className="rbui-slider__input"
        value={values[index]}
      />
    </span>
  );
};

SliderThumb.displayName = 'SliderThumb';
